<!-- Scheduled Order Cycles Dialog -->
<div class="scheduled-order-cycles-dialog">
  <!-- Summary Stats with Filters -->
  <div class="mb-4 p-3 border-round-lg surface-100">
    <!-- Loading State for Stats -->
    <div *ngIf="isLoading" class="flex align-items-center gap-3 flex-wrap">
      <p-skeleton width="120px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="100px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="130px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="110px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="90px" height="2.5rem" borderRadius="9999px" />
    </div>

    <!-- Actual Stats -->
    <div *ngIf="!isLoading" class="flex align-items-center gap-3 flex-wrap">
      <div
        class="stat-badge stat-badge-success"
        [class.stat-badge-active]="statusFilter === 'success'"
        (click)="toggleFilter('success')"
      >
        <i class="pi pi-check-circle"></i>
        <span class="stat-number">{{ getStatusStats().success }}</span>
        <span class="stat-label">Success</span>
      </div>
      <div
        class="stat-badge stat-badge-error"
        [class.stat-badge-active]="statusFilter === 'failed'"
        (click)="toggleFilter('failed')"
      >
        <i class="pi pi-times-circle"></i>
        <span class="stat-number">{{ getStatusStats().failed }}</span>
        <span class="stat-label">Failed</span>
      </div>
      <div
        class="stat-badge stat-badge-info"
        [class.stat-badge-active]="statusFilter === 'processing'"
        (click)="toggleFilter('processing')"
      >
        <i class="pi pi-spinner"></i>
        <span class="stat-number">{{ getStatusStats().processing }}</span>
        <span class="stat-label">Processing</span>
      </div>
      <div
        class="stat-badge stat-badge-info"
        [class.stat-badge-active]="statusFilter === 'queued'"
        (click)="toggleFilter('queued')"
      >
        <i class="pi pi-clock"></i>
        <span class="stat-number">{{ getStatusStats().queued }}</span>
        <span class="stat-label">Queued</span>
      </div>
      <div
        class="stat-badge stat-badge-neutral"
        [class.stat-badge-active]="statusFilter === 'all'"
        (click)="toggleFilter('all')"
      >
        <i class="pi pi-list"></i>
        <span class="stat-number">{{ getStatusStats().total }}</span>
        <span class="stat-label">All</span>
      </div>
    </div>
  </div>

  <!-- Loading State with Skeleton -->
  <div *ngIf="isLoading" class="skeleton-container">
    <div class="skeleton-row" *ngFor="let i of [1, 2, 3, 4, 5]">
      <p-skeleton width="120px" height="2.5rem" borderRadius="8px" />
      <p-skeleton width="140px" height="1.5rem" />
      <p-skeleton width="140px" height="1.5rem" />
      <p-skeleton width="140px" height="1.5rem" />
      <p-skeleton width="80px" height="1.5rem" />
      <p-skeleton width="80px" height="2rem" borderRadius="6px" />
      <p-skeleton width="100px" height="2rem" borderRadius="6px" />
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!isLoading && filteredCycles.length === 0" class="empty-state">
    <div class="empty-state-content">
      <i class="pi pi-inbox"></i>
      <h3>No Results</h3>
      <p>No processing cycles match the current filter.</p>
    </div>
  </div>

  <!-- Cycles Table -->
  <div
    *ngIf="!isLoading && filteredCycles.length > 0"
    class="scheduled-order-cycles-table"
  >
    <p-table
      [value]="filteredCycles"
      [scrollable]="true"
      scrollHeight="flex"
      scrollDirection="both"
      styleClass="p-datatable-sm"
      [rowHover]="true"
      dataKey="Id"
    >
      <ng-template pTemplate="header">
        <tr>
          <th style="min-width: 120px">Processing ID</th>
          <th style="min-width: 140px">Created Date</th>
          <th style="min-width: 140px">Start Date</th>
          <th style="min-width: 140px">End Date</th>
          <th style="min-width: 80px">Total</th>
          <th style="min-width: 100px">Charged</th>
          <th style="min-width: 90px">Status</th>
          <th style="min-width: 70px">Retries</th>
          <!-- Actions Column (Frozen Right) -->
          <th
            style="min-width: 100px; width: 100px"
            pFrozenColumn
            [frozen]="true"
            alignFrozen="right"
          >
            Actions
          </th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-cycle>
        <tr
          [ngClass]="
            'log-type-' + (cycle.Status === 'Failed' ? 'error' : 'event')
          "
        >
          <!-- Processing ID -->
          <td data-label="Processing ID">
            <p-chip
              [label]="(cycle.ProcessingId || cycle.Id).toString()"
              styleClass="processing-id-chip"
            />
          </td>

          <!-- Created Date -->
          <td class="text-sm" data-label="Created Date">
            {{ formatDate(cycle.CreatedUtc) }}
          </td>

          <!-- Start Date -->
          <td class="text-sm" data-label="Start Date">
            {{ formatDate(cycle.StartDateUtc) }}
          </td>

          <!-- End Date -->
          <td class="text-sm" data-label="End Date">
            {{ formatDate(cycle.EndDateUtc) }}
          </td>

          <!-- Total -->
          <td class="text-sm" data-label="Total">
            {{ formatCurrency(cycle.Total) }}
          </td>

          <!-- Charged Total -->
          <td class="text-sm font-medium" data-label="Charged">
            {{ formatCurrency(cycle.ChargedTotal) }}
          </td>

          <!-- Status -->
          <td data-label="Status">
            <p-tag
              [value]="cycle.Status"
              [severity]="getCycleSeverity(cycle.Status)"
              styleClass="text-sm"
            />
          </td>

          <!-- Retry Count -->
          <td class="text-center" data-label="Retries">
            <span
              class="inline-flex align-items-center justify-content-center w-2rem h-2rem border-circle"
              [ngClass]="{
                'bg-red-100 text-red-700': cycle.RetryCount > 0,
                'bg-gray-100 text-gray-600': cycle.RetryCount === 0,
              }"
            >
              {{ cycle.RetryCount || 0 }}
            </span>
          </td>

          <!-- Actions (Frozen Right) -->
          <td
            data-label="Actions"
            pFrozenColumn
            [frozen]="true"
            alignFrozen="right"
            class="actions-column"
          >
            <p-button
              icon="pi pi-eye"
              label="View"
              size="small"
              severity="secondary"
              [outlined]="true"
              styleClass="action-btn"
              (onClick)="goToProcessingCycleView(cycle.Id)"
              pTooltip="View detailed processing cycle information"
              tooltipPosition="left"
            />
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="9" class="text-center p-4">
            <div class="flex flex-column align-items-center gap-2 text-500">
              <i class="pi pi-inbox text-4xl"></i>
              <p class="text-lg font-medium">No Results</p>
              <p class="text-sm">
                No processing cycles match the current filter.
              </p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  <!-- Help Text -->
  <div class="mt-4 p-3 border-round surface-100">
    <div class="text-sm text-600">
      <i class="pi pi-info-circle mr-1"></i>
      <strong>About Processing Cycles:</strong> This shows all processing
      attempts for this scheduled order, including successful orders, failed
      attempts, and retry counts. Each cycle represents an attempt to process
      this scheduled order into an actual order on your platform.
    </div>
  </div>
</div>
